import { DivIcon, LatLngBounds, LatLngExpression } from 'leaflet';
import { Gate } from '../types/Gate';
import { GateSummary, GateSummaryWithCluster } from '../types/GateSummary';

export const FALLBACK_CENTER: LatLngExpression = [51.5074, -0.1278]; // Default to London if no gates returned

/** A marker's class names, for anything that needs to tell a highlighted marker from a plain one. */
const GATE_MARKER_CLASS = 'gate-marker';
const GATE_MARKER_HIGHLIGHT_CLASS = 'gate-marker-highlighted';

export interface GateIconOptions {
  /** Draw a blue halo at the marker's foot: the gate is on a route the list has ticked. */
  highlighted?: boolean;
}

export function createGateIcon(
  type: 'start' | 'end',
  label: string,
  markerIconPng: string,
  endMarkerIconPng: string,
  { highlighted = false }: GateIconOptions = {},
): DivIcon {
  // The pin's tip is the anchor, so the halo is centred on the bottom edge of the image.
  const halo = highlighted
    ? `<span style="position: absolute; bottom: -15px; left: 50%; transform: translateX(-50%); width: 30px;
         height: 30px; border-radius: 9999px; background: rgba(37, 99, 235, 0.3); border: 2px solid #2563eb;"></span>`
    : '';
  return new DivIcon({
    className: highlighted ? `${GATE_MARKER_CLASS} ${GATE_MARKER_HIGHLIGHT_CLASS}` : GATE_MARKER_CLASS,
    html: `
      <div style="position: relative; display: inline-flex; align-items: center; justify-content: center;">
        ${halo}
        <img src="${type === 'start' ? markerIconPng : endMarkerIconPng}" alt="Gate marker" style="width: 25px; height: 41px; filter: drop-shadow(0 1px 2px rgba(0,0,0,0.25)); position: relative;" />
        <span style="position: absolute; top: -12px; left: 50%; transform:
         translateX(-50%); min-width: 20px; padding: 2px 6px; border-radius: 9999px; border: 2px solid #ffffff;
          background: ${
            type === 'start' ? '#0f766e' : '#b91c1c'
          }; color: #ffffff; font-size: 12px; font-weight: 700; line-height: 1; text-align: center;">${label}</span>
      </div>
    `,
    iconAnchor: [12, 41],
    popupAnchor: [0, -36],
    tooltipAnchor: [0, -36],
  });
}

export function transformGateToSummary(gate: Gate, index: number): GateSummary | null {
  const startLat = gate.gates_lat?.[0];
  const startLon = gate.gates_lon?.[0];
  if (typeof startLat !== 'number' || typeof startLon !== 'number') {
    return null;
  }

  const endLat = gate.gates_lat?.[1];
  const endLon = gate.gates_lon?.[1];
  const end =
    typeof endLat === 'number' && typeof endLon === 'number' ? ([endLat, endLon] as LatLngExpression) : null;

  const labelCandidate =
    (gate.address && gate.address.trim()) ||
    (gate.description && gate.description.trim()) ||
    (gate.notes && gate.notes.trim()) ||
    (gate.route_id && gate.route_id.trim());
  const label = labelCandidate && labelCandidate.length > 0 ? labelCandidate : gate.id;
  const routeDescription = gate.route_description?.trim() || '';
  const popupLabel = (gate.notes && gate.notes.trim()) || routeDescription || label;

  return {
    id: gate.id,
    start: [startLat, startLon] as LatLngExpression,
    end,
    label,
    routeDescription,
    popupLabel,
    isValid: gate.valid,
    gate,
    order: index + 1,
  };
}

export function transformGateToSummaryWithCluster(gate: Gate, index: number): GateSummaryWithCluster | null {
  const base = transformGateToSummary(gate, index);
  if (!base) return null;
  return {
    ...base,
    cluster: gate.cluster ?? 0,
  };
}

export function calculateAllBounds(gateSummaries: GateSummary[]): LatLngBounds | null {
  if (!gateSummaries.length) return null;
  const positions: LatLngExpression[] = [];
  gateSummaries.forEach((gate) => {
    positions.push(gate.start);
    if (gate.end) positions.push(gate.end);
  });
  return positions.length ? new LatLngBounds(positions) : null;
}

export function calculateSelectedBounds(selectedGate: GateSummary | null): LatLngBounds | null {
  if (!selectedGate) return null;
  const points: LatLngExpression[] = [selectedGate.start];
  if (selectedGate.end) points.push(selectedGate.end);
  return new LatLngBounds(points);
}
